/* eslint prefer-const: ["error", {"ignoreReadBeforeAssign": true}] */

import { Component, ChangeDetectionStrategy, Output, EventEmitter, Input } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { FlagsService, FLAGS } from 'src/app/services/flags.service';
import { PencilButtonThemeEnum } from 'src/app/standalones/components/pencil-button/pencil-button.component';

import {
  SUBSCRIPTION_TYPES,
  HubspotContactProperties,
  SubscriptionEvent,
  PaymentPlan,
  Subscription,
  BillingStatus,
} from 'src/app/models/payment';
import { BillingStrategy, BillingFrequency } from '@pncl/common-models';
import { User } from 'src/app/models/user';
import { ModalManagerService } from 'src/app/services/modal-manager.service';
import { PaymentService } from 'src/app/services/payment.service';
import { ReferralService } from 'src/app/services/referral.service';
import { DateFormatEnum } from 'src/app/pipes/dateLocale.pipe';
import { PlanDetailsService } from '../../../services/plan-details.service';
import { UserService } from '../../../services/user.service';
import { SettingsConstantsTransformer } from '../../settings.constants';

@UntilDestroy()
@Component({
  selector: 'app-user-settings-payment',
  templateUrl: './user-settings-payment.component.html',
  styleUrls: ['./user-settings-payment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsPaymentComponent {
  @Input() isMobile = false;
  @Input() PANELS?: SettingsConstantsTransformer['PANELS'];
  @Output() goBack = new EventEmitter<void>();
  @Output() closePanel = new EventEmitter();

  protected readonly DateFormatEnum = DateFormatEnum;
  protected readonly BillingStatus = SUBSCRIPTION_TYPES;
  protected readonly PencilButtonThemeEnum = PencilButtonThemeEnum;
  protected readonly BillingFrequency = BillingFrequency;

  protected user?: User;

  protected currentPlan$ = new BehaviorSubject<PaymentPlan | undefined>(undefined);

  // TODO: Remove this activeLegacyPlan$ property after all pro guest users are migrated to the new billing
  protected activeLegacyPlan$ = new BehaviorSubject<Partial<PaymentPlan> | undefined>(undefined);

  protected isInstitutionUser$ = new BehaviorSubject(false);
  protected subHeaderTitle$ = new BehaviorSubject('');
  protected subHeaderDescription$ = new BehaviorSubject('');
  protected billingFrequency$ = new BehaviorSubject(BillingFrequency.Monthly);
  protected selectedBillingStrategy$ = new BehaviorSubject(BillingStrategy.USAGE_BASED);
  protected nextBillingDate$ = new BehaviorSubject<Date | undefined>(undefined);

  private cachedPlans$ = this.planDetailsService.plans$;
  protected filteredPlans$: Observable<PaymentPlan[]>;
  constructor(
    public referralService: ReferralService,
    private userService: UserService,
    private paymentService: PaymentService,
    public modalManagerService: ModalManagerService,
    public planDetailsService: PlanDetailsService,
    private translateService: TranslateService,
    private flagsService: FlagsService,
  ) {
    this.filteredPlans$ = combineLatest([
      this.billingFrequency$,
      this.selectedBillingStrategy$,
      this.cachedPlans$,
    ]).pipe(
      map(([frequency, strategy, plans]) =>
        plans.filter(
          (plan) =>
            !plan.isHiddenFromPortal &&
            (plan.billingStrategy === BillingStrategy.CUSTOM_BASED ||
              (plan.billingStrategy === strategy && plan.billingFrequency === frequency)),
        ),
      ),
    );

    combineLatest([this.userService.user, this.cachedPlans$])
      .pipe(untilDestroyed(this))
      .subscribe(([res, plans]) => {
        this.user = res?.user;
        this.isInstitutionUser$.next(!!this.user?.institution);

        // TODO: This handleManualSubscriptionRedirect logic should be removed once "pro guest" users are migrated.
        this.handleManualSubscriptionRedirect(this.user);

        if (!this.user?.institution) {
          // TODO: This setLegacyPlan logic should be removed once "pro guest" users are migrated.
          this.setLegacyPlan(this.user?.billingStatus);
        }

        if (this.user?.institution) {
          const subscription = <Subscription>this.user?.institution?.billingStatus?.subscription;
          if (subscription) {
            const plan = plans.find((p) => p.planId && p.planId === subscription.plan);
            if (plan) {
              this.currentPlan$.next(plan);
            } else {
              // TODO: This else block should be removed once
              // the custom Automatic Billing Institution plans no longer have active subscriptions.
              this.setLegacyInstitutionPlan(this.user.institution.billingStatus);
            }
          }
        }

        this.billingFrequency$.next(
          this.currentPlan$.value?.billingFrequency || BillingFrequency.Monthly,
        );

        this.updateHeaderTexts(this.user);

        this.paymentService
          .updateHubspotContactProperties(HubspotContactProperties.APP_PLANS_LAST_VISIT)
          .pipe(untilDestroyed(this))
          .subscribe();
      });
  }

  // TODO: This handleManualSubscriptionRedirect logic should be removed once "pro guest" users are migrated.
  // This is a temporary workaround to prevent users who were manually promoted to a "pro" plan
  // (e.g., for piloting or trials through the admin portal) from accessing this page.
  // These accounts have fake billing data that can break the UI and checkout process.
  // The long-term solution is to find these users, correct their data, or migrate them to proper institutional accounts.
  private handleManualSubscriptionRedirect(user?: User): void {
    if (user?.subscriptionType === SUBSCRIPTION_TYPES.PAID) {
      const { subscription } = user.billingStatus || {};

      if (
        subscription?.subscriptionEvents?.[0]?.eventType ===
        SubscriptionEvent.subscription_updated_manually
      ) {
        this.goBack.emit();
      }
    }
  }

  // TODO: Remove this setLegacyPlan logic after all pro guest users are migrated to the new billing
  private setLegacyPlan(billingStatus?: BillingStatus): void {
    const subscription = <Subscription>billingStatus?.subscription;
    this.nextBillingDate$.next(
      subscription?.nextBillingAt ? moment(subscription.nextBillingAt * 1000).toDate() : undefined,
    );

    const subscriptionPlan = subscription?.plan;
    if (!subscriptionPlan) {
      return;
    }

    const monthlyPlanID = this.flagsService.isFlagEnabled(FLAGS.ENABLE_BILLING)
      ? (this.flagsService.featureFlagsVariables.enable_billing.monthly_plan_id as string)
      : '';

    this.activeLegacyPlan$.next({
      planId: subscriptionPlan,
      name: subscriptionPlan === monthlyPlanID ? 'Pro (Monthly plan)' : 'Pro (Annual plan)',
      billingFrequency:
        subscriptionPlan === monthlyPlanID ? BillingFrequency.Monthly : BillingFrequency.Annually,
    });
  }

  // TODO: This setLegacyInstitutionPlan logic should be removed once
  // the custom Automatic Billing Institution plans no longer have active subscriptions.
  private setLegacyInstitutionPlan(billingStatus?: BillingStatus): void {
    const subscription = <Subscription>billingStatus?.subscription;
    this.nextBillingDate$.next(
      subscription?.nextBillingAt ? moment(subscription.nextBillingAt * 1000).toDate() : undefined,
    );

    const subscriptionPlan = subscription?.plan;
    if (!subscriptionPlan) {
      return;
    }

    this.activeLegacyPlan$.next({
      planId: subscriptionPlan,
      name: 'Custom Institution Plan',
      billingFrequency: BillingFrequency.Monthly,
    });
  }

  private updateHeaderTexts(user?: User): void {
    const forceBillDate = user?.billingStatus?.forceBillDate;
    const subscriptionType = user?.subscriptionType;
    const remainingFreeDays = moment(forceBillDate).diff(Date.now(), 'd', true);

    // Default values used for free tier
    let title = this.translateService.instant("You're on our free tier.");
    let description = this.translateService.instant(
      'Select one of our plans below to access our premium features.',
    );

    if (subscriptionType === SUBSCRIPTION_TYPES.TRIAL) {
      if (remainingFreeDays && remainingFreeDays > 0) {
        const days = remainingFreeDays.toFixed(0);
        title = `${this.translateService.instant(
          'You have',
        )} ${days} ${this.translateService.instant('days left in trial')}`;

        description = this.translateService.instant(
          'Select one of the plans below to keep enjoying premium features on Pencil Spaces.',
        );
      } else {
        title = this.translateService.instant('Your trial is over');
      }
    }

    this.subHeaderTitle$.next(title);
    this.subHeaderDescription$.next(description);
  }

  protected createPortalSession(): void {
    this.paymentService.openBufferWindow('Management Portal');
    this.paymentService.handlePortalSession(this.user?.billingStatus?.subscription?.plan);
  }

  protected updateBillingFrequency(frequency: BillingFrequency) {
    this.billingFrequency$.next(frequency);
  }

  protected updateBillingStrategy(strategy: BillingStrategy) {
    if (strategy === BillingStrategy.USAGE_BASED) {
      this.billingFrequency$.next(BillingFrequency.Monthly);
    }
    this.selectedBillingStrategy$.next(strategy);
  }
}
